import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, firstValueFrom } from 'rxjs';
import { environment } from 'src/environments/environment';

export type CupoNivel = 'PRE' | 'COMPLETO';

/** Motivos por los que el consumo puede no aplicar. */
export type CupoConsumoMotivo = 'PRE' | 'SIN_CUPO' | 'COMPLETO' | 'INACTIVO';

/**
 * Estado del cupo de registro de un usuario.
 * `GET /cupo-registro/:usuarioId`.
 */
export interface CupoRegistroEstado {
  existe: boolean;
  usuarioId?: number;
  nivel?: CupoNivel;
  /** `false` = el tope ya no aplica. */
  activo?: boolean;
  bloqueado?: boolean;
  /** String cuando `bloqueado === true`. */
  motivoBloqueo?: string | null;
  tope?: number;
  updatedAt?: string;
}

/**
 * Consumo del cupo (solo relevante mientras el cliente sigue PRE-activo).
 * `GET /cupo-registro/:usuarioId/consumo`.
 */
export interface CupoRegistroConsumo {
  /** `true` SOLO si el cliente sigue PRE con cupo activo. */
  aplica: boolean;
  motivo: CupoConsumoMotivo;
  nivel: CupoNivel;
  tope: number | null;
  /** Salidas CLP acumuladas (tipo_transaccion != 'ABONO'). */
  consumido: number | null;
  disponible: number | null;
  /** 0..100 (null si no aplica). Puede tocar 100 con cap del backend. */
  porcentajeUtilizado: number | null;
}

/**
 * Salidas (gasto) totales del usuario en CLP, independiente del cupo.
 * `GET /cupo-registro/:usuarioId/salidas`. Suma de movimientos
 * `tipo_transaccion <> 'ABONO'`. Cuando el cliente es PRE, coincide con
 * el `consumido` del endpoint de consumo.
 */
export interface CupoSalidas {
  usuarioId: number;
  salidasClp: number;
}

/**
 * Resultado de bloquear/desbloquear. `ok: false` cuando el usuario no
 * tiene cupo de registro (trae `message`).
 */
export interface CupoAccionResult {
  ok: boolean;
  usuarioId?: number;
  bloqueado?: boolean;
  motivoBloqueo?: string | null;
  message?: string;
}

/**
 * Cupo del propio usuario (JWT del cliente) — `GET /cupo-registro/me`.
 * `puedeOperar` es la señal que habilita las vistas/acciones de aprobado
 * para un PRE con cupo válido y desbloqueado.
 */
export interface CupoRegistroMe {
  existe: boolean;
  nivel?: CupoNivel;
  bloqueado?: boolean;
  /** El tope de 14M rige (mostrar banner). */
  aplicaTope?: boolean;
  tope?: number | null;
  consumido?: number | null;
  disponible?: number | null;
  porcentajeUtilizado?: number | null;
  /** Habilita las funcionalidades de aprobado (PRE operativo). */
  puedeOperar?: boolean;
}

/**
 * Cupo de registro (compliance).
 *
 * Endpoints de OPERADOR (`:usuarioId`, `/consumo`, `/salidas`,
 * `/bloquear`, `/desbloquear`) requieren JWT + grupo `aguacate_operator`.
 * El endpoint `/me` usa el JWT del propio cliente y alimenta el gating de
 * "PRE operativo" en el layout del cliente. El token lo inyecta el
 * interceptor estándar en ambos casos.
 */
@Injectable({ providedIn: 'root' })
export class CupoRegistroService {
  private baseUrl = `${environment.apiNest}cupo-registro`;

  /**
   * Estado del cupo del cliente actual, cacheado para el gating del
   * layout. `null` = no cargado o error → fail-closed (no habilita
   * operar). Lo consumen el guard y el home vía `puedeOperar`.
   */
  private meSubject = new BehaviorSubject<CupoRegistroMe | null>(null);
  me$: Observable<CupoRegistroMe | null> = this.meSubject.asObservable();
  private meLoaded = false;

  constructor(private http: HttpClient) {}

  /** Snapshot del cupo propio (null si no cargado). */
  get me(): CupoRegistroMe | null {
    return this.meSubject.value;
  }

  /**
   * `true` SOLO si el backend dijo explícitamente `puedeOperar: true`.
   * Fail-closed: ante error/sin dato NO habilita operar.
   */
  get puedeOperar(): boolean {
    return this.meSubject.value?.puedeOperar === true;
  }

  /** Fetch crudo del cupo propio. */
  getMe(): Promise<CupoRegistroMe> {
    return firstValueFrom(this.http.get<CupoRegistroMe>(`${this.baseUrl}/me`));
  }

  /**
   * Carga el cupo propio una vez por sesión (cache). El guard la usa en
   * cada navegación sin re-pegarle a la API salvo `force`.
   */
  async ensureMe(force = false): Promise<CupoRegistroMe | null> {
    if (this.meLoaded && !force) return this.meSubject.value;
    return this.refreshMe();
  }

  /** Re-consulta el cupo propio y actualiza el subject. Fail-closed. */
  async refreshMe(): Promise<CupoRegistroMe | null> {
    try {
      const me = await this.getMe();
      this.meSubject.next(me);
      this.meLoaded = true;
      return me;
    } catch {
      // Si no podemos leer el cupo (404/403/red), NO habilitamos operar.
      this.meSubject.next(null);
      this.meLoaded = true;
      return null;
    }
  }

  /** Reset al logout: evita que el cupo de un usuario filtre a otro. */
  clearMe(): void {
    this.meLoaded = false;
    this.meSubject.next(null);
  }

  /** Estado del cupo: nivel, activo, bloqueado, tope. */
  getEstado(usuarioId: number): Promise<CupoRegistroEstado> {
    return firstValueFrom(
      this.http.get<CupoRegistroEstado>(`${this.baseUrl}/${usuarioId}`),
    );
  }

  /**
   * Consumo del cupo. El backend solo calcula la suma cuando el cliente
   * es PRE-activo; en cualquier otro caso responde `aplica: false` con
   * los montos en `null` y el `motivo` correspondiente.
   */
  getConsumo(usuarioId: number): Promise<CupoRegistroConsumo> {
    return firstValueFrom(
      this.http.get<CupoRegistroConsumo>(`${this.baseUrl}/${usuarioId}/consumo`),
    );
  }

  /** Gasto acumulado en CLP (salidas), sirva o no el cupo. */
  getSalidas(usuarioId: number): Promise<CupoSalidas> {
    return firstValueFrom(
      this.http.get<CupoSalidas>(`${this.baseUrl}/${usuarioId}/salidas`),
    );
  }

  /** Bloquea el acceso al tope (queda pre-registrado pero sin operar). */
  bloquear(usuarioId: number, motivo: string): Promise<CupoAccionResult> {
    return firstValueFrom(
      this.http.post<CupoAccionResult>(
        `${this.baseUrl}/${usuarioId}/bloquear`,
        { motivo },
      ),
    );
  }

  /** Desbloquea: vuelve a permitir operar dentro de su tope. */
  desbloquear(usuarioId: number): Promise<CupoAccionResult> {
    return firstValueFrom(
      this.http.post<CupoAccionResult>(
        `${this.baseUrl}/${usuarioId}/desbloquear`,
        {},
      ),
    );
  }
}
